// The /jh route inside the RRT client. This small file is the only part of /jh in the main bundle. It checks
// the existing login the same way Components/PrivateRoute does, then loads the /jh pages as a separate chunk
// the first time someone opens /jh. Nothing here runs on any other page.
//
// This file and JhPages.tsx are the only /jh files that import from the RRT client: the login state, the
// sign-out action, the stored login, the API address and the loading spinner. /jh sends the token itself with fetch (GET only);
// it does not use or change the axios defaults.
import { Component, lazy, ReactNode, Suspense, useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import FullScreenLoader from "../../Components/FullScreenLoader/FullScreenLoader";
import Constants from "../../Constants/Constants";
import { logoutUser } from "../../Redux/Auth/AuthActions";
import { AppDispatch, RootState } from "../../Redux/store";
import { getStorage } from "../../Util/storageHelper";

const JhPages = lazy(() => import("./JhPages"));

// A tab left open across a deploy asks for a /jh script file that has since been renamed. This says so in one
// plain line with a Reload button, in place of the app's raw error page.
class JhLoadGuard extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <p style={{ padding: 24 }}>
        This page has been updated. Reload to carry on.{" "}
        <button type="button" onClick={() => window.location.reload()}>Reload</button>
      </p>
    );
  }
}

function JhRoute() {
  const dispatch = useDispatch<AppDispatch>();
  const { token, user } = useSelector((state: RootState) => state.auth);
  const tokenRef = useRef(token);
  tokenRef.current = token;
  const getToken = useCallback(() => tokenRef.current, []);
  // 401: the login has run out. Sign out the same way the header's Logout does; the check below then sends
  // the person to the login page. 403: signed in but not an admin. Stay signed in; /jh shows its own message.
  const onUnauthorized = useCallback(
    (status: number) => {
      if (status === 401) dispatch(logoutUser());
    },
    [dispatch]
  );

  // /jh drops everything it has read when the token above changes or when this route leaves the screen (the
  // header's Logout does both). One case this tab cannot see by itself: someone signed out, or signed in as
  // someone else, in ANOTHER tab. The stored login then differs from this tab's, and the page is loaded again so
  // that it shows only what the person now signed in may see. Only while /jh is on screen.
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== null && event.key !== "userData") return;
      const stored = getStorage("userData");
      if (((stored && stored.token) || null) !== (tokenRef.current || null)) window.location.reload();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  if (!token || !user) {
    return <Navigate to="/admin/login" replace />;
  }

  return (
    <JhLoadGuard>
      <Suspense fallback={<FullScreenLoader />}>
        <JhPages
          apiBase={`${Constants.apiUrl}/jh`}
          basePath="/jh"
          getToken={getToken}
          onUnauthorized={onUnauthorized}
        />
      </Suspense>
    </JhLoadGuard>
  );
}

export default JhRoute;
